import React from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Folder, FileText, Database, CaretRight, CircleNotch, House, MagnifyingGlass,
} from "@phosphor-icons/react";
import { Card, Input } from "../epbcs/common";
import { ReportRunner, SqlViewer } from "./ReportRunner";

const TYPE_META = {
  folder: { icon: Folder, cls: "text-[#C8A24B]" },
  report: { icon: FileText, cls: "text-[#0B1E3F]" },
  data_model: { icon: Database, cls: "text-[#1F6B4A]" },
};

export default function CatalogSection() {
  const [folder, setFolder] = React.useState("/");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [selected, setSelected] = React.useState(null);
  const [q, setQ] = React.useState("");

  const load = React.useCallback((f) => {
    setLoading(true); setErr("");
    api.get("/reporting/catalog", { params: { folder: f } })
      .then((r) => { setItems(r.data.items || []); setFolder(r.data.folder); })
      .catch((e) => {
        setItems([]);
        setErr(e?.response?.data?.detail || "Could not load catalog folder");
      })
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { load("/"); }, [load]);

  const crumbs = folder === "/" ? [] : folder.split("/").filter(Boolean);
  const filtered = items.filter((it) => !q || it.name.toLowerCase().includes(q.toLowerCase()));

  const open = (it) => {
    if (it.type === "folder") { setQ(""); load(it.path); }
    else if (it.type === "report" || it.type === "data_model") setSelected(it);
    else toast.info(`Item type "${it.type}" cannot be opened here`);
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <Card
        title="Catalog Browser"
        subtitle="Live view of the BI Publisher catalog over SOAP. Click a folder to descend; click a report to run it."
        testid="catalog-browser-card"
      >
        <div className="flex items-center gap-1 flex-wrap text-[12px] mb-3 pb-2 border-b border-slate-100" data-testid="catalog-breadcrumb">
          <button onClick={() => load("/")} data-testid="catalog-crumb-root"
            className="flex items-center gap-1 text-[#0B1E3F] hover:text-[#C8A24B] font-semibold">
            <House size={13} weight="bold" /> Catalog
          </button>
          {crumbs.map((c, i) => (
            <React.Fragment key={i}>
              <CaretRight size={10} className="text-slate-400" />
              <button data-testid={`catalog-crumb-${i}`}
                onClick={() => load("/" + crumbs.slice(0, i + 1).join("/"))}
                className="text-[#0B1E3F] hover:text-[#C8A24B] font-semibold">{c}</button>
            </React.Fragment>
          ))}
        </div>
        <div className="relative mb-3">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="catalog-search-input" className="pl-8" placeholder="Filter this folder…"
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {loading && (
          <div className="flex items-center gap-2 text-[12px] text-slate-500 py-6 justify-center" data-testid="catalog-loading">
            <CircleNotch size={16} className="animate-spin" /> Fetching folder from Fusion…
          </div>
        )}
        {err && !loading && (
          <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="catalog-error">{err}</div>
        )}
        {!loading && !err && (
          <div className="divide-y divide-slate-100 max-h-[520px] overflow-auto" data-testid="catalog-items">
            {filtered.length === 0 && (
              <div className="text-[12px] text-slate-400 py-6 text-center">Empty folder</div>
            )}
            {filtered.map((it) => {
              const meta = TYPE_META[it.type] || { icon: FileText, cls: "text-slate-400" };
              const Icon = meta.icon;
              const active = selected?.path === it.path;
              return (
                <button key={it.path} onClick={() => open(it)}
                  data-testid={`catalog-item-${it.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`w-full flex items-center gap-3 px-2 py-2 text-left transition-colors border-l-2 ${
                    active ? "border-[#C8A24B] bg-[#0B1E3F]/[0.04]" : "border-transparent hover:bg-slate-50"
                  }`}>
                  <Icon size={16} weight={it.type === "folder" ? "fill" : "bold"} className={meta.cls} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] font-medium text-[#0F172A] truncate">{it.name}</span>
                    {it.last_modified && (
                      <span className="block text-[10px] text-slate-400">{it.last_modified}</span>
                    )}
                  </span>
                  <span className={`text-[9px] tracking-[0.15em] uppercase font-bold px-1.5 py-0.5 border ${
                    it.type === "folder" ? "text-[#C8A24B] border-[#C8A24B]/40"
                      : it.type === "report" ? "text-[#0B1E3F] border-[#0B1E3F]/30"
                      : "text-[#1F6B4A] border-[#1F6B4A]/30"
                  }`}>
                    {it.type.replace("_", " ")}
                  </span>
                  {it.type === "folder" && <CaretRight size={12} className="text-slate-400" />}
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <Card
        title={selected ? (selected.type === "report" ? "Run Report" : "Data Model SQL") : "Details"}
        subtitle={selected ? undefined : "Select a report or data model on the left."}
        testid="catalog-detail-card"
      >
        {!selected && (
          <div className="text-[12px] text-slate-400 py-10 text-center" data-testid="catalog-detail-empty">
            Nothing selected yet — browse the catalog and pick a report to execute or a data model to inspect its SQL.
          </div>
        )}
        {selected?.type === "report" && <ReportRunner path={selected.path} name={selected.name} />}
        {selected?.type === "data_model" && <SqlViewer path={selected.path} />}
      </Card>
    </div>
  );
}
